import type {ErrorEvent} from "@sentry/react";

// Local-development noise dropped before an event reaches Sentry. Each pattern
// fires only while the Vite dev-server is running (an HMR message) or in React's
// dev build (a deprecation warning), so a built production bundle can never emit
// them — matching here cannot hide a real production error. Deliberate errors
// from a local session (e.g. verifying the Sentry integration) still report.
export const devNoiseIgnorePatterns: (string | RegExp)[] = [
  // Single-argument Vite HMR messages surfaced through captureConsole, e.g.
  // "[vite] failed to connect to websocket". A two-argument
  // `console.error("[vite]", err)` does not match here — see
  // `isViteConsoleNoise`.
  /^\[vite\]/,
  // React dev-mode console warning, not a fault.
  "findDOMNode is deprecated",
];

/** Exception type Tolgee raises when a translation record cannot be loaded. */
const RECORD_FETCH_ERROR_TYPE = "RecordFetchError";

/**
 * Tolgee's fetch backend reports a non-OK response by throwing an `Error` whose
 * message is the resolved request URL and the status, separated by a space. It
 * becomes the cause of the `RecordFetchError` that reaches the app, and Sentry's
 * `linkedErrors` integration lifts it into its own exception value.
 */
const RECORD_FETCH_STATUS_PATTERN = /^(\S+) (\d{3})$/;

/**
 * A translation-record fetch that was answered with 403.
 *
 * Records are fetched from the page's own origin, so on a customer's custom
 * domain the request is screened by whatever CDN or filtering proxy fronts that
 * domain before it ever reaches us. A 403 is that intermediary's verdict on the
 * visitor: the records are world-readable, and a record we do not have is
 * answered with 404, so a 403 cannot have been produced by our own delivery
 * path. Nothing about the app or the record is wrong, and no change on our side
 * alters the outcome.
 *
 * Only 403 is treated this way. Any other failure — a 404, a 5xx, a transport
 * error with no status at all — remains reportable, so a genuine fault in
 * translation delivery still surfaces.
 *
 * `recordBaseUrl` is the absolute form of the configured Tolgee backend prefix;
 * every record URL is that prefix followed by a path segment.
 */
export function isForbiddenRecordFetch(event: ErrorEvent, recordBaseUrl: string): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  if (!values.some(value => value.type === RECORD_FETCH_ERROR_TYPE)) return false;
  const base = recordBaseUrl.replace(/\/+$/, "");
  return values.some(value => {
    const match = value.value ? RECORD_FETCH_STATUS_PATTERN.exec(value.value) : null;
    if (match === null) return false;
    const [, url, status] = match;
    return status === "403" && url.startsWith(`${base}/`);
  });
}

// Vite's dev client reports a failed HMR chunk fetch as
// `console.error("[vite]", err)`. captureConsole turns that two-argument form
// into an *exception* event whose value is the bare `TypeError` message
// ("error loading dynamically imported module: …?t=<timestamp>"), so the
// `[vite]` tag survives only as the leading entry of `extra.arguments` — out of
// reach of `ignoreErrors`, which matches the exception value rather than the raw
// console arguments. Matching on that tag drops the whole family: the tag is
// emitted only by the Vite dev server, so it cannot fire against a built
// production bundle.
export function isViteConsoleNoise(event: ErrorEvent): boolean {
  const args = event.extra?.arguments;
  if (!Array.isArray(args) || args.length === 0) return false;
  const first = args[0];
  return typeof first === "string" && first.startsWith("[vite]");
}

// How Chromium, Firefox and WebKit each word a failure to fetch a dynamically
// imported module — any `import()` chunk (route splits, lazy components, vendor
// chunks), not only the route splits behind `React.lazy`.
const DYNAMIC_IMPORT_FAILURE_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/;

function isDynamicImportFailure(event: ErrorEvent): boolean {
  return (
    event.exception?.values?.some(value => value.value != null && DYNAMIC_IMPORT_FAILURE_PATTERN.test(value.value)) ??
    false
  );
}

/**
 * A dynamically imported module that failed to fetch while the browser reported
 * itself offline.
 *
 * `navigator.onLine === false` is a definite verdict: the browser knows it has
 * no connectivity, so the chunk request never had a chance and nothing we serve
 * changes the outcome — safe to drop on any tab, visible or hidden. The converse
 * does not hold: a captive portal, dropped link, or filtering proxy still
 * reports `onLine === true`, so an import failure captured while nominally
 * online stays reportable here and falls to the other gates.
 */
export function isOfflineImportFailure(event: ErrorEvent, online: boolean): boolean {
  if (online) return false;
  return isDynamicImportFailure(event);
}

/**
 * A dynamically imported module that failed to fetch while the tab was hidden.
 *
 * These are dominated by navigations abandoned in flight: the user follows a
 * link or backgrounds the tab, the document begins tearing down, and the chunk
 * request for the page being left is aborted — surfacing as a bare `TypeError`
 * with no stack. No one saw the destination and nothing re-attempts the load, so
 * the event is pure noise.
 *
 * The visibility gate is what makes this safe. An import failure captured while
 * the tab is *visible* means someone is waiting on a screen that did not load
 * (offline, a content blocker, a stale deploy), which stays reportable. The
 * residual, as in `index.tsx`: a persistent failure (content blocker, stale
 * deploy) captured while merely backgrounded is dropped too, and because
 * `React.lazy` caches the rejection, returning to that route never re-requests
 * it — accepted as the cost of silencing the dominant navigation noise.
 *
 * This mirrors the boot-failure gate in `index.tsx`, extended to any `import()`
 * chunk — whose failures reach Sentry through `captureConsole` rather than that
 * catch, so that gate never sees them.
 */
export function isHiddenTabImportFailure(event: ErrorEvent, visibilityState: string): boolean {
  if (visibilityState !== "hidden") return false;
  return isDynamicImportFailure(event);
}

// Sentry's browser-API instrumentation (`browserApiErrors`) wraps native APIs —
// `setTimeout`, `addEventListener`, XHR handlers — and tags any error it catches
// from them with a `mechanism.type` under `auto.browser.browserapierrors.`, the
// suffix naming the wrapped API or the patched function. That bucket is mostly
// first-party: a throwing React event handler lands here too, because the
// delegated root listener is registered through the wrapped `addEventListener`.
// So the mechanism alone cannot decide.
//
// The noise this drops is an injected endpoint-agent / browser-extension script
// that has monkey-patched a native API and throws from its own hook: the error
// reaches us through the instrumentation, but the throw happens entirely outside
// our code. Our own bundle (and the Sentry SDK bundled with it) is served from
// `firstPartyOrigin`; an injected script throws from a frame with a foreign
// filename (`<anonymous>` for eval'd code, `chrome-extension://`, another host).
//
// The throw site is the innermost frame — frames run oldest-first, so it is the
// last one that names a source (native frames carry no filename). Classifying on
// that frame keeps a real handler error, which throws from our own first-party
// code, while dropping a throw that originates in injected code.
export function isThirdPartyInstrumentationNoise(event: ErrorEvent, firstPartyOrigin: string): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  // `linkedErrors` prepends causes and stamps the mechanism onto `values[0]`, so
  // scan every value rather than assuming which one carries it.
  if (!values.some(value => value.mechanism?.type.startsWith("auto.browser.browserapierrors."))) return false;
  const frames = values.flatMap(value => value.stacktrace?.frames ?? []);
  for (let i = frames.length - 1; i >= 0; i--) {
    const filename = frames[i].filename;
    if (filename) return !filename.startsWith(firstPartyOrigin);
  }
  return false;
}

// A DOMException (code 8) thrown from `removeChild`/`insertBefore` during React's
// commit phase. React owns the DOM within a root and detaches nodes by their
// recorded parent; a browser translation feature (Chrome's built-in Google
// Translate, or an extension) rewrites text runs into `<font>` wrappers, moving
// those nodes out from under React. When React then commits a deletion or
// reorder, the node is no longer the child React recorded and the operation
// throws. React catches it, discards and re-renders the tree, and logs via
// `console.error` — so it arrives through `captureConsole` with a pure
// react-dom stack. The mutation originates entirely outside our code and the UI
// self-recovers, so the report is noise. Matching the two `Node` operations
// (not the `NotFoundError` type alone) keeps it to this DOM-reconciliation family.
const DOM_RECONCILIATION_NOISE_PATTERN = /Failed to execute '(removeChild|insertBefore)' on 'Node'/;

export function isExternalDomMutationNoise(event: ErrorEvent): boolean {
  return (
    event.exception?.values?.some(value => value.value != null && DOM_RECONCILIATION_NOISE_PATTERN.test(value.value)) ??
    false
  );
}
